import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, ButtonBase, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Stack, TextField, Typography,
} from '@mui/material';
import {
  marketplaceCategoryLabel, type ArtistApplication,
} from '@eprintsmart/shared';
import { getArtwork, reviewArtistApplication, watchPendingApplications } from '../services/marketplace';

/**
 * People asking to sell here, and the decision on each.
 *
 * Shows the work, not a row of filenames. The whole judgement is "is this good
 * enough and is it really theirs", and neither question can be answered from a
 * title — so the three previews are the page and everything else is caption.
 *
 * Approving is irreversible in the way that matters: it mints the designer,
 * takes the handle, publishes all three pieces and emails them. Rejecting
 * deletes their files. Both are behind a confirmation for that reason.
 */

const AGE = (ms: number) => {
  const hours = Math.floor((Date.now() - ms) / 3_600_000);
  if (hours < 1) return 'just now';
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};

/**
 * Preview URLs, read from the artwork documents the upload trigger wrote.
 *
 * Read rather than constructed from a bucket name and a path convention: the
 * trigger already publishes the exact URL it used, and reproducing that formula
 * here would break silently the day the bucket, the format or the size names
 * change — showing broken images on the one screen where seeing the work IS the
 * task.
 */
function useArtworkPreviews(artworkIds: string[]): Record<string, string | undefined> {
  const [previews, setPreviews] = useState<Record<string, string | undefined>>({});
  const key = artworkIds.join(',');

  useEffect(() => {
    let live = true;
    void Promise.all(artworkIds.map((id) => getArtwork(id))).then((rows) => {
      if (!live) return;
      const next: Record<string, string | undefined> = {};
      for (const art of rows) {
        if (art) next[art.id] = art.previews?.md ?? art.previews?.thumb;
      }
      setPreviews((prev) => ({ ...prev, ...next }));
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return previews;
}

export function ApplicationQueue() {
  /** The piece being looked at full size, or null. */
  const [zoomed, setZoomed] = useState<{ url: string; title: string } | null>(null);
  const [rows, setRows] = useState<ArtistApplication[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<ArtistApplication | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => watchPendingApplications(setRows), []);

  const previews = useArtworkPreviews(rows.flatMap((r) => r.pieces.map((p) => p.artworkId)));

  const decide = async (
    application: ArtistApplication,
    action: 'APPROVE' | 'REJECT',
    why?: string,
  ) => {
    setBusy(true);
    setError(null);
    try {
      await reviewArtistApplication({
        applicationId: application.id,
        action,
        ...(why ? { reason: why } : {}),
      });
      setRejecting(null);
      setReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Applications{rows.length > 0 && ` (${rows.length})`}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Approving takes the handle, publishes all three pieces and emails them. Declining deletes
        the files they sent and tells them why — so write something you would say to a person.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {rows.length === 0 ? (
        <Paper variant="outlined">
          <Box p={2}>
            <Typography variant="body2" color="text.secondary">
              Nobody waiting. Applications arrive from /artists/apply.
            </Typography>
          </Box>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {rows.map((application) => (
            <Paper key={application.id} variant="outlined">
              <Box p={2}>
                <Stack direction="row" alignItems="baseline" spacing={2} flexWrap="wrap">
                  <Typography variant="subtitle1">{application.displayName}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    /artist/{application.handle} · {application.email} · {AGE(Number(application.createdAt))}
                  </Typography>
                </Stack>

                <Typography variant="body2" sx={{ mt: 1, maxWidth: '46rem' }}>
                  {application.about}
                </Typography>

                {application.links?.map((l) => (
                  <Typography key={l.url} variant="caption" display="block" sx={{ mt: 0.5 }}>
                    <a href={l.url} target="_blank" rel="nofollow noopener noreferrer">{l.label}</a>
                  </Typography>
                ))}

                <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                  {application.pieces.map((piece) => (
                    <Box key={piece.artworkId} sx={{ width: 200 }}>
                      {/* Watermarked preview, never the original — the print
                          file stays private even from this screen. */}
                      {previews[piece.artworkId] ? (
                        /*
                         * Click to see it properly. The thumbnail is square and
                         * `cover`, so it CROPS — on a portrait sketch that can
                         * hide a third of the piece, and "is this good enough"
                         * is not a question anyone should answer from a crop.
                         *
                         * ButtonBase rather than an onClick on the img: it
                         * carries the focus ring, Enter and Space for free, so
                         * the expand is reachable without a mouse.
                         */
                        <ButtonBase
                          onClick={() => setZoomed({ url: previews[piece.artworkId]!, title: piece.title })}
                          focusRipple
                          aria-label={`View ${piece.title} full size`}
                          sx={{
                            width: '100%', borderRadius: 1, display: 'block',
                            '&:hover img': { opacity: 0.85 },
                          }}
                        >
                          <img
                            src={previews[piece.artworkId]}
                            alt={piece.title}
                            style={{
                              width: '100%', aspectRatio: '1', objectFit: 'cover',
                              borderRadius: 4, display: 'block', background: '#f2f2f2',
                              transition: 'opacity 120ms',
                            }}
                          />
                        </ButtonBase>
                      ) : (
                        <Box sx={{
                          width: '100%', aspectRatio: '1', borderRadius: 1, bgcolor: 'action.hover',
                          display: 'grid', placeItems: 'center',
                        }}>
                          {/* PDF, EPS and SVG get no preview — the trigger only
                              rasterises images. Say so rather than show a void. */}
                          <Typography variant="caption" color="text.secondary">
                            no preview
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="body2" sx={{ mt: 0.5 }}>{piece.title}</Typography>
                      {piece.tags.length > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {piece.tags.slice(0, 4).join(' · ')}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                        {piece.categoryIds.length === 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            everything
                          </Typography>
                        ) : piece.categoryIds.map((c) => (
                          <Chip key={c} size="small" variant="outlined"
                            label={marketplaceCategoryLabel(c)} />
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button variant="contained" size="small" disabled={busy}
                    onClick={() => void decide(application, 'APPROVE')}>
                    Approve &amp; publish
                  </Button>
                  <Button color="error" size="small" disabled={busy}
                    onClick={() => { setRejecting(application); setReason(''); }}>
                    Decline
                  </Button>
                </Stack>
              </Box>
            </Paper>
          ))}
        </Stack>
      )}

      <Dialog open={Boolean(rejecting)} onClose={() => setRejecting(null)} fullWidth maxWidth="sm">
        <DialogTitle>Decline {rejecting?.displayName}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This deletes the three files they sent, and emails them. There is no undo — they would
            have to apply again.
          </Alert>
          <TextField
            label="What should we tell them?"
            fullWidth
            multiline
            rows={4}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            helperText="Sent to them word for word. Optional, but a refusal with no reason is worse than none."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)}>Cancel</Button>
          <Button color="error" variant="contained" disabled={busy}
            onClick={() => rejecting && void decide(rejecting, 'REJECT', reason.trim() || undefined)}>
            Decline and delete
          </Button>
        </DialogActions>
      </Dialog>

      {/*
        * One piece, big enough to judge.
        *
        * `contain`, not `cover`: the thumbnail crops to a square and the point
        * of opening this is to see what the crop hid. Sized against the
        * viewport so a tall sketch and a wide one both fit without scrolling.
        *
        * Still the watermarked preview — expanding shows the work, never the
        * print file, which stays private even from this screen.
        */}
      <Dialog
        open={!!zoomed}
        onClose={() => setZoomed(null)}
        maxWidth="lg"
        aria-labelledby="zoomed-title"
      >
        <DialogTitle id="zoomed-title" sx={{ pr: 6 }}>
          {zoomed?.title}
          <IconButton
            onClick={() => setZoomed(null)}
            aria-label="Close"
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {zoomed && (
            <img
              src={zoomed.url}
              alt={zoomed.title}
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '75vh',
                margin: '0 auto',
                objectFit: 'contain',
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
